import { useState } from "react";
import { Star, BadgeCheck } from "lucide-react";

const ACCENT = "#B8923A"; // champagne stars matching site palette

const SOURCE_COLOR = {
  Hitched: { bg: "#C28A7E", fg: "#fff" },
  Google: { bg: "#4285F4", fg: "#fff" },
  Facebook: { bg: "#1877F2", fg: "#fff" },
  Bridebook: { bg: "#7BA478", fg: "#fff" },
  "Guides for Brides": { bg: "#A05A5C", fg: "#fff" },
};

function initialsOf(name = "") {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

const TRUNC = 220;

export default function ReviewCard({ r, testId, sourceTestId }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = (r.text || "").length > TRUNC;
  const display = expanded || !isLong ? r.text : r.text.slice(0, TRUNC).replace(/\s+\S*$/, "") + "…";
  const colour = SOURCE_COLOR[r.source] || { bg: "#1C1917", fg: "#fff" };

  return (
    <article
      data-testid={testId}
      className="bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)] flex flex-col p-7 md:p-8"
    >
      <div className="flex items-center gap-1 mb-5" aria-label="5 out of 5 stars">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-3.5 h-3.5" fill={ACCENT} stroke={ACCENT} strokeWidth={1} />
        ))}
      </div>

      <p className="font-serif text-stone-900 text-[1.08rem] md:text-[1.15rem] leading-[1.6] flex-1">
        <span className="font-serif italic text-stone-700">&ldquo;</span>
        {display}
        <span className="font-serif italic text-stone-700">&rdquo;</span>
      </p>

      {isLong && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="mt-5 self-start text-[0.7rem] tracking-[0.22em] uppercase text-[#A88E6A] hover:text-stone-900 transition-colors"
          data-testid={`${testId}-read-more`}
        >
          Read more
        </button>
      )}

      <div className="mt-8 pt-5 border-t border-stone-200 flex items-center gap-3">
        {r.profilePhotoUri ? (
          <img
            src={r.profilePhotoUri}
            alt={`${r.name} profile photo`}
            className="w-10 h-10 rounded-full object-cover"
            loading="lazy"
            width="40"
            height="40"
          />
        ) : (
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center font-serif text-sm"
            style={{ background: colour.bg, color: colour.fg }}
            aria-hidden="true"
          >
            {initialsOf(r.name)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-serif text-stone-900 text-[0.98rem] truncate">{r.name}</p>
          <p className="overline text-stone-500 mt-0.5 truncate">{r.date}</p>
        </div>
        <a
          href={r.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={sourceTestId}
          title={`Verified review on ${r.source}`}
          className="inline-flex items-center text-stone-500 hover:text-stone-900 transition-colors"
        >
          <BadgeCheck className="w-[16px] h-[16px] text-[#7BA478]" strokeWidth={1.6} />
        </a>
      </div>
    </article>
  );
}
